import { db } from '../firebase';
import { collection, doc, getDocs, setDoc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';

export interface Promoter {
  id: string; // custom id or code
  name: string;
  email: string;
  phone?: string;
  code: string; // unique code, e.g. "GROW-BOB"
  referralMethod: 'Flyer' | 'Word of Mouth' | 'Event' | 'Social Media' | 'Other';
  referralMethodDetails?: string;
  createdAt: string;
  totalPaidCommissions: number; // custom payouts recorded
  commissionPercentage?: number; // custom referral commission percentage, default: 20
}

export interface PromoterPayout {
  id: string;
  promoterId: string;
  amount: number;
  date: string;
  notes?: string;
}

export const getAllPromoters = async (): Promise<Promoter[]> => {
  try {
    const q = query(collection(db, 'promoters'), orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    const promoters: Promoter[] = [];
    snap.forEach((docSnap) => {
      promoters.push({ id: docSnap.id, ...docSnap.data() } as Promoter);
    });
    return promoters;
  } catch (err) {
    console.error('Error fetching promoters:', err);
    return [];
  }
};

export const addPromoter = async (promoter: Promoter): Promise<void> => {
  try {
    const docRef = doc(db, 'promoters', promoter.id);
    await setDoc(docRef, promoter);
  } catch (err) {
    console.error('Error adding promoter:', err);
    throw err;
  }
};

export const updatePromoter = async (id: string, data: Partial<Promoter>): Promise<void> => {
  try {
    const docRef = doc(db, 'promoters', id);
    await updateDoc(docRef, data);
  } catch (err) {
    console.error('Error updating promoter:', err);
    throw err;
  }
};

export const deletePromoter = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, 'promoters', id);
    await deleteDoc(docRef);
  } catch (err) {
    console.error('Error deleting promoter:', err);
    throw err;
  }
};

export const getPromoterPayouts = async (promoterId: string): Promise<PromoterPayout[]> => {
  try {
    const q = query(collection(db, 'promoters', promoterId, 'payouts'), orderBy('date', 'desc'));
    const snap = await getDocs(q);
    const payouts: PromoterPayout[] = [];
    snap.forEach((docSnap) => {
      payouts.push({ id: docSnap.id, ...docSnap.data() } as PromoterPayout);
    });
    return payouts;
  } catch (err) {
    console.error('Error fetching payouts:', err);
    return [];
  }
};

export const addPromoterPayout = async (promoterId: string, payout: PromoterPayout): Promise<void> => {
  try {
    const docRef = doc(db, 'promoters', promoterId, 'payouts', payout.id);
    await setDoc(docRef, payout);
    
    // Also increment totalPaidCommissions on the promoter document
    const promRef = doc(db, 'promoters', promoterId);
    await setDoc(promRef, {
      totalPaidCommissions: (payout.amount || 0)
    }, { merge: true });
  } catch (err) {
    console.error('Error adding payout:', err);
    throw err;
  }
};
